import { useCallback, useEffect, useState } from "react";
import {
  defaultTwoFactorPolicy,
  getDefaultLoginMethod,
  getEnabledLoginMethods,
  getTwoFactorStorageKey,
  MAX_SELECTED_LOGIN_METHODS,
  normalizeCredentialId,
  readTwoFactorRecord,
  type FidoCredentialRecord,
  type TwoFactorPolicy,
  type TwoFactorStorageRecord,
  type VerificationLoginMethod,
  isVerificationLoginMethod,
} from "../Constants/webauthnMock";
import { registerSecurityKey } from "../Services/webauthnClient";
import { webauthnApi, type BackendCredential } from "../Services/webauthnApi";
import {
  fetchTwoFactorSettings,
  updateTwoFactorSettings,
} from "../Services/twoFactorSettingsApi";

export type TwoFactorMethod = "sms" | "fido" | "both";
export type LoginMethod = VerificationLoginMethod;

export type { VerificationLoginMethod };

export type FidoCredential = FidoCredentialRecord;

export interface TwoFactorSettings {
  method: TwoFactorMethod;
  preferredLogin: LoginMethod;
  selectedLoginMethods: VerificationLoginMethod[];
  googleAuthenticatorConfigured: boolean;
  credentials: FidoCredential[];
  policy: TwoFactorPolicy;
  countryDialCode?: string;
  areaCode?: string;
  phoneLocalNumber?: string;
  phoneNumber?: string;
}

export const mockBackendPolicy = defaultTwoFactorPolicy();

const isMockMode = (): boolean =>
  process.env.REACT_APP_FIDO_MOCK === "true";

const mapBackendCredential = (cred: BackendCredential): FidoCredential => ({
  id: cred.id,
  label: cred.label,
  createdAt: cred.createdAt,
  transports: cred.transports?.length ? cred.transports : ["usb"],
});

const toSettings = (record: TwoFactorStorageRecord): TwoFactorSettings => ({
  method: record.method || (record.credentials.length > 0 ? "both" : "sms"),
  preferredLogin: record.preferredLogin,
  selectedLoginMethods: record.selectedLoginMethods,
  googleAuthenticatorConfigured: record.googleAuthenticatorConfigured === true,
  credentials: record.credentials,
  policy: record.policy,
});

const readSettings = (storageKey: string): TwoFactorSettings => {
  const accountKey = storageKey.replace(/^valut:2fa:/, "");
  return toSettings(readTwoFactorRecord(accountKey));
};

const persistLocal = (storageKey: string, next: TwoFactorSettings) => {
  const record: TwoFactorStorageRecord = {
    credentials: next.credentials.map((item) => ({
      ...item,
      transports: item.transports?.length ? item.transports : ["usb"],
    })),
    preferredLogin: next.preferredLogin,
    selectedLoginMethods: next.selectedLoginMethods,
    googleAuthenticatorConfigured: next.googleAuthenticatorConfigured,
    method: next.method,
    policy: next.policy || mockBackendPolicy,
  };
  localStorage.setItem(storageKey, JSON.stringify(record));
};

export const useTwoFactorSettings = (accountKey?: string) => {
  const storageKey = getTwoFactorStorageKey(accountKey || "anonymous");
  const [settings, setSettings] = useState<TwoFactorSettings>(() => {
    const stored = readSettings(storageKey);
    // Non-mock: never show cached USB credentials until the API confirms they belong
    // to the authenticated person (avoids cross-user leakage via localStorage).
    if (!isMockMode()) {
      return { ...stored, credentials: [] };
    }
    return stored;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const stored = readSettings(storageKey);
    if (!isMockMode()) {
      setSettings({ ...stored, credentials: [] });
      return;
    }
    setSettings(stored);
  }, [storageKey]);

  const saveSettings = useCallback(
    (next: TwoFactorSettings, options?: { syncRemote?: boolean }) => {
      persistLocal(storageKey, next);
      setSettings(next);

      if (isMockMode() || options?.syncRemote === false) {
        return;
      }

      void updateTwoFactorSettings({
        preferredLogin: next.preferredLogin,
        selectedLoginMethods: next.selectedLoginMethods,
        googleAuthenticatorConfigured: next.googleAuthenticatorConfigured,
        countryDialCode: next.countryDialCode,
        areaCode: next.areaCode,
        phoneLocalNumber: next.phoneLocalNumber,
        phoneNumber: next.phoneNumber,
      }).catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("[useTwoFactorSettings] updateTwoFactorSettings failed", error);
        }
      });
    },
    [storageKey]
  );

  const applyPersonPreferences = useCallback(
    (personPrefs: Awaited<ReturnType<typeof fetchTwoFactorSettings>>) => {
      setSettings((prev) => {
        const selectedLoginMethods = (personPrefs.selectedLoginMethods || [])
          .filter(isVerificationLoginMethod)
          .slice(0, MAX_SELECTED_LOGIN_METHODS);

        const safeSelected =
          selectedLoginMethods.length > 0
            ? selectedLoginMethods
            : prev.selectedLoginMethods.length > 0
              ? prev.selectedLoginMethods
              : (["email"] as VerificationLoginMethod[]);

        let preferredLogin: VerificationLoginMethod = safeSelected[0] || "email";
        if (
          personPrefs.preferredLogin &&
          isVerificationLoginMethod(personPrefs.preferredLogin) &&
          safeSelected.includes(personPrefs.preferredLogin)
        ) {
          preferredLogin = personPrefs.preferredLogin;
        }

        const next: TwoFactorSettings = {
          ...prev,
          preferredLogin,
          selectedLoginMethods: safeSelected,
          googleAuthenticatorConfigured:
            personPrefs.googleAuthenticatorConfigured === true,
          method: prev.credentials.length > 0 ? "both" : "sms",
          countryDialCode: personPrefs.countryDialCode || prev.countryDialCode || "",
          areaCode: personPrefs.areaCode || prev.areaCode || "",
          phoneLocalNumber:
            personPrefs.phoneLocalNumber || prev.phoneLocalNumber || "",
          phoneNumber: personPrefs.phoneNumber || prev.phoneNumber || "",
        };
        persistLocal(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const fetchPersonPreferences = useCallback(async () => {
    if (isMockMode()) return;
    try {
      const personPrefs = await fetchTwoFactorSettings();
      applyPersonPreferences(personPrefs);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[useTwoFactorSettings] fetchTwoFactorSettings failed", error);
      }
    }
  }, [applyPersonPreferences]);

  const fetchCredentials = useCallback(async () => {
    if (isMockMode()) return;

    setLoading(true);
    try {
      const credentials = (await webauthnApi.getCredentials()).map(mapBackendCredential);

      setSettings((prev) => {
        let selectedLoginMethods: VerificationLoginMethod[] =
          credentials.length > 0 && !prev.selectedLoginMethods.includes("fido")
            ? prev.selectedLoginMethods.length < MAX_SELECTED_LOGIN_METHODS
              ? [...prev.selectedLoginMethods, "fido" as VerificationLoginMethod]
              : prev.selectedLoginMethods
            : prev.selectedLoginMethods;

        const requiredMethods = (prev.policy?.required || []).filter(
          (m): m is VerificationLoginMethod =>
            ["email", "sms", "whatsapp", "fido", "google_authenticator"].includes(m)
        );
        for (const req of requiredMethods) {
          if (!selectedLoginMethods.includes(req)) {
            selectedLoginMethods = [...selectedLoginMethods, req];
          }
        }

        const next: TwoFactorSettings = {
          ...prev,
          credentials,
          method: credentials.length > 0 ? "both" : "sms",
          selectedLoginMethods,
        };

        persistLocal(storageKey, next);

        // Keep person.TwoFactorSelectedMethods in sync when USB keys imply fido.
        if (
          selectedLoginMethods.includes("fido") &&
          !prev.selectedLoginMethods.includes("fido")
        ) {
          void updateTwoFactorSettings({
            preferredLogin: next.preferredLogin,
            selectedLoginMethods: next.selectedLoginMethods,
            googleAuthenticatorConfigured: next.googleAuthenticatorConfigured,
          }).catch(() => undefined);
        }

        return next;
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[useTwoFactorSettings] fetchCredentials failed — clearing USB list for this person",
          error
        );
      }
      // Fail closed: never keep another person's (or stale) USB keys on screen.
      setSettings((prev) => {
        const next = { ...prev, credentials: [] };
        const selectedLoginMethods = next.selectedLoginMethods.filter((m) => m !== "fido");
        const safeSelected =
          selectedLoginMethods.length > 0
            ? selectedLoginMethods
            : (["email"] as VerificationLoginMethod[]);
        const preferredLogin = safeSelected.includes(next.preferredLogin)
          ? next.preferredLogin
          : safeSelected[0] || "email";
        const cleared: TwoFactorSettings = {
          ...next,
          selectedLoginMethods: safeSelected,
          preferredLogin,
          method: "sms",
        };
        persistLocal(storageKey, cleared);
        return cleared;
      });
    } finally {
      setLoading(false);
    }
  }, [storageKey]);

  useEffect(() => {
    if (isMockMode()) return;
    void (async () => {
      await fetchPersonPreferences();
      await fetchCredentials();
    })();
  }, [storageKey, fetchPersonPreferences, fetchCredentials]);

  const hasRegisteredFidoKey = useCallback(
    () => settings.credentials.length > 0,
    [settings.credentials]
  );

  const registerFidoKey = useCallback(
    async (
      label?: string,
      options?: {
        userId?: string;
        username?: string;
        registrationAttempt?: number;
      }
    ) => {
      const userId = options?.userId || accountKey || "anonymous";
      const username = options?.username || userId;

      const { credentialId, transports } = await registerSecurityKey({
        userId,
        username,
        label: label?.trim() || `Chave USB ${settings.credentials.length + 1}`,
        registrationAttempt: options?.registrationAttempt,
      });

      if (!isMockMode()) {
        await fetchCredentials();
        // Persist updated selected methods (fido) to person after register.
        setSettings((prev) => {
          const selectedLoginMethods: VerificationLoginMethod[] =
            prev.selectedLoginMethods.includes("fido")
              ? prev.selectedLoginMethods
              : prev.selectedLoginMethods.length < MAX_SELECTED_LOGIN_METHODS
                ? [...prev.selectedLoginMethods, "fido" as VerificationLoginMethod]
                : prev.selectedLoginMethods;
          const next: TwoFactorSettings = {
            ...prev,
            method: "both",
            selectedLoginMethods,
            preferredLogin: selectedLoginMethods.includes(prev.preferredLogin)
              ? prev.preferredLogin
              : "fido",
          };
          persistLocal(storageKey, next);
          void updateTwoFactorSettings({
            preferredLogin: next.preferredLogin,
            selectedLoginMethods: next.selectedLoginMethods,
            googleAuthenticatorConfigured: next.googleAuthenticatorConfigured,
          }).catch(() => undefined);
          return next;
        });

        const synced = readTwoFactorRecord(accountKey).credentials;
        return (
          synced[synced.length - 1] ?? {
            id: normalizeCredentialId(credentialId),
            label: label?.trim() || `Chave USB ${settings.credentials.length + 1}`,
            createdAt: new Date().toISOString(),
            transports: transports.length ? transports : ["usb"],
          }
        );
      }

      const credential: FidoCredential = {
        id: normalizeCredentialId(credentialId),
        label: label?.trim() || `Chave USB ${settings.credentials.length + 1}`,
        createdAt: new Date().toISOString(),
        transports: transports.length ? transports : ["usb"],
      };

      const credentials = [...settings.credentials, credential];
      const selectedLoginMethods: VerificationLoginMethod[] =
        settings.selectedLoginMethods.includes("fido")
          ? settings.selectedLoginMethods
          : settings.selectedLoginMethods.length < MAX_SELECTED_LOGIN_METHODS
            ? [...settings.selectedLoginMethods, "fido" as VerificationLoginMethod]
            : settings.selectedLoginMethods;

      const next: TwoFactorSettings = {
        ...settings,
        credentials,
        method: "both",
        selectedLoginMethods,
        preferredLogin: selectedLoginMethods.includes(settings.preferredLogin)
          ? settings.preferredLogin
          : "fido",
        policy: settings.policy || mockBackendPolicy,
      };

      saveSettings(next);
      return credential;
    },
    [accountKey, settings, saveSettings, fetchCredentials, storageKey]
  );

  const removeFidoKey = useCallback(
    async (credentialId: string) => {
      if (!isMockMode()) {
        try {
          await webauthnApi.removeCredential(credentialId);
        } catch (error) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[useTwoFactorSettings] removeCredential API failed", error);
          }
          throw error;
        }
      }

      const credentials = settings.credentials.filter(
        (item) => item.id !== credentialId
      );
      const selectedLoginMethods =
        credentials.length === 0
          ? settings.selectedLoginMethods.filter((method) => method !== "fido")
          : settings.selectedLoginMethods;
      const safeSelected =
        selectedLoginMethods.length > 0
          ? selectedLoginMethods
          : ["email" as VerificationLoginMethod];

      const next: TwoFactorSettings = {
        ...settings,
        credentials,
        method: credentials.length > 0 ? settings.method : "sms",
        selectedLoginMethods: safeSelected,
        preferredLogin: safeSelected.includes(settings.preferredLogin)
          ? settings.preferredLogin
          : safeSelected[0] || "email",
      };
      saveSettings(next);
    },
    [settings, saveSettings]
  );

  const setPreferredLogin = useCallback(
    (preferredLogin: LoginMethod) => {
      const selectedLoginMethods: VerificationLoginMethod[] =
        settings.selectedLoginMethods.includes(preferredLogin)
          ? settings.selectedLoginMethods
          : settings.selectedLoginMethods.length < MAX_SELECTED_LOGIN_METHODS
            ? [...settings.selectedLoginMethods, preferredLogin]
            : [
                preferredLogin,
                ...settings.selectedLoginMethods.slice(0, MAX_SELECTED_LOGIN_METHODS - 1),
              ];

      saveSettings({
        ...settings,
        preferredLogin,
        selectedLoginMethods,
      });
    },
    [settings, saveSettings]
  );

  const setSelectedLoginMethods = useCallback(
    (selectedLoginMethods: VerificationLoginMethod[]) => {
      const nextMethods = selectedLoginMethods.slice(
        0,
        MAX_SELECTED_LOGIN_METHODS
      );
      const preferredLogin = nextMethods.includes(settings.preferredLogin)
        ? settings.preferredLogin
        : nextMethods[0] || "email";

      saveSettings({
        ...settings,
        selectedLoginMethods: nextMethods,
        preferredLogin,
      });
    },
    [settings, saveSettings]
  );

  const configureGoogleAuthenticator = useCallback(() => {
    const selectedLoginMethods: VerificationLoginMethod[] =
      settings.selectedLoginMethods.includes("google_authenticator")
        ? settings.selectedLoginMethods
        : settings.selectedLoginMethods.length < MAX_SELECTED_LOGIN_METHODS
          ? [
              ...settings.selectedLoginMethods,
              "google_authenticator" as VerificationLoginMethod,
            ]
          : settings.selectedLoginMethods;

    saveSettings({
      ...settings,
      googleAuthenticatorConfigured: true,
      selectedLoginMethods,
      preferredLogin: selectedLoginMethods.includes(settings.preferredLogin)
        ? settings.preferredLogin
        : selectedLoginMethods[0] || "email",
    });
  }, [settings, saveSettings]);

  const removeGoogleAuthenticator = useCallback(() => {
    const selectedLoginMethods = settings.selectedLoginMethods.filter(
      (method) => method !== "google_authenticator"
    );
    const safeSelected =
      selectedLoginMethods.length > 0
        ? selectedLoginMethods
        : ["email" as VerificationLoginMethod];

    saveSettings({
      ...settings,
      googleAuthenticatorConfigured: false,
      selectedLoginMethods: safeSelected,
      preferredLogin: safeSelected.includes(settings.preferredLogin)
        ? settings.preferredLogin
        : safeSelected[0] || "email",
    });
  }, [settings, saveSettings]);

  const setPersonPhone = useCallback(
    (parts: {
      countryDialCode?: string;
      areaCode?: string;
      phoneLocalNumber?: string;
    }) => {
      saveSettings({
        ...settings,
        countryDialCode: parts.countryDialCode ?? settings.countryDialCode ?? "",
        areaCode: parts.areaCode ?? settings.areaCode ?? "",
        phoneLocalNumber:
          parts.phoneLocalNumber ?? settings.phoneLocalNumber ?? "",
      });
    },
    [settings, saveSettings]
  );

  const resolveEnabledLoginMethods = useCallback(
    (options: { isPilot?: boolean } = {}) =>
      getEnabledLoginMethods(settings.policy, {
        isPilot: options.isPilot,
        hasFidoKey: settings.credentials.length > 0,
        hasGoogleAuthenticator: settings.googleAuthenticatorConfigured,
        selectedLoginMethods: settings.selectedLoginMethods,
      }),
    [
      settings.credentials.length,
      settings.googleAuthenticatorConfigured,
      settings.policy,
      settings.selectedLoginMethods,
    ]
  );

  const resolveDefaultLoginMethod = useCallback(
    (options: { isPilot?: boolean } = {}) =>
      getDefaultLoginMethod(settings.policy, {
        isPilot: options.isPilot,
        hasFidoKey: settings.credentials.length > 0,
        hasGoogleAuthenticator: settings.googleAuthenticatorConfigured,
        preferredLogin: settings.preferredLogin,
        selectedLoginMethods: settings.selectedLoginMethods,
      }),
    [
      settings.credentials.length,
      settings.googleAuthenticatorConfigured,
      settings.policy,
      settings.preferredLogin,
      settings.selectedLoginMethods,
    ]
  );

  return {
    settings,
    loading,
    mockBackendPolicy,
    hasRegisteredFidoKey,
    fetchCredentials,
    fetchPersonPreferences,
    registerFidoKey,
    /** @deprecated use registerFidoKey */
    registerMockFidoKey: registerFidoKey,
    removeFidoKey,
    setPreferredLogin,
    setSelectedLoginMethods,
    setPersonPhone,
    configureGoogleAuthenticator,
    removeGoogleAuthenticator,
    getEnabledLoginMethods: resolveEnabledLoginMethods,
    getDefaultLoginMethod: resolveDefaultLoginMethod,
  };
};
